import { IconSearch } from '@maia/icons';
import { Input, Pagination, Table, ToggleButtonGroup } from '@maia/ui';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useEffect } from 'react';
import { Claims, getClaimsOptions } from './queryOptions';
import { useFilter } from '../../../utils/filter/filter';
import { useClaimsTable } from './tableOptions';
import { useParams } from '@tanstack/react-router';

type ClaimsTableProps = {
  claims: Claims;
};

export function ClaimsTable(props: ClaimsTableProps) {
  const { claims } = props;
  const { guid } = useParams({
    from: '/_authenticated/insurances/$guid/_layout/claims',
  });
  const {
    state,
    dispatch: setSearch,
    mappedState,
  } = useFilter('/insurances/$guid/claims');
  const { data } = useQuery(getClaimsOptions(guid, mappedState, claims));

  const table = useClaimsTable(claims ?? data);

  const { sorting } = table.getState();

  useEffect(() => {
    setSearch({ type: 'setSort', payload: sorting });
  }, [setSearch, sorting]);

  const debouncedQuery = debounce(
    (payload: string) => setSearch({ type: 'setQuery', payload }),
    500,
  );

  return (
    <Table
      table={table}
      className="grid-cols-[min-content_auto_auto_auto_min-content_min-content]"
      filterSlot={
        <div className="flex gap-x-24">
          <Input
            icon={<IconSearch />}
            placeholder="Zoeken"
            className="w-full max-w-[550px] mr-auto"
            onChange={(event) => debouncedQuery(event.target.value)}
            defaultValue={state.search}
          />
          <ToggleButtonGroup
            items={[
              { label: 'Alle', value: 'all' },
              { label: 'Openstaand', value: 'in_progress' },
              { label: 'Afgewezen', value: 'is_closed' },
            ]}
            onChange={(status) =>
              setSearch({ type: 'setFilter', payload: ['status', status] })
            }
          />
        </div>
      }
      paginationSlot={
        data?.pagination ? (
          <Pagination
            {...data.pagination}
            onLimitChange={(limit) =>
              setSearch({ type: 'setLimit', payload: limit })
            }
            onPageChange={(page) =>
              setSearch({ type: 'paginate', payload: page })
            }
          />
        ) : null
      }
    />
  );
}
